import React, { useState } from 'react';
import { SimilarCompaniesResponse, getSimilarCompanies } from '../../services/openai';
import CompanyForm from './components/CompanyForm';
import ResultsTable from './components/ResultsTable';
import { downloadCSV } from './utils/csvExport';
import '../../styles/CompanySearch.css';

/**
 * Main component for the Company Search feature
 */
const CompanySearch: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SimilarCompaniesResponse | null>(null);

  /**
   * Handle form submission to search for similar companies
   * @param companyName The name of the company
   * @param contactPerson The key contact person of the company
   */
  const handleSubmit = async (companyName: string, contactPerson: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getSimilarCompanies(companyName, contactPerson);
      setResults(data);
    } catch (err) {
      console.error('Error fetching similar companies:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while fetching similar companies');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (results) {
      try {
        downloadCSV(results);
      } catch (err) {
        console.error('Error exporting CSV:', err);
        setError(err instanceof Error ? err.message : 'An error occurred while exporting CSV');
      }
    }
  };

  return (
    <div className="company-search-container">
      <h1 className="company-search-title">Company Search</h1>
      <p className="company-search-description">
        Enter a company name and key contact person to find similar companies and their equipment needs.
      </p>
      
      {/* Error message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {/* Company search form */}
      <CompanyForm 
        onSubmit={handleSubmit} 
        isLoading={isLoading} 
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Searching for similar companies...</p>
        </div>
      )}
      
      {/* Results table */}
      {!isLoading && results && (
        <ResultsTable 
          data={results} 
          onExportCSV={handleExportCSV} 
        />
      )}
    </div>
  );
};

export default CompanySearch;
